import { Clipboard } from '@angular/cdk/clipboard';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgForm } from '@angular/forms';
import { VueFormError, VueModalComponent, VueServerMessage, VueServerMessageType, VueTranslationService } from '@vue/vueframework';
import { TedBackUpRequest } from '../shared/models/ted-backup-request.model';
import { TedBackUpService } from '../shared/services/tedhub-backup.service';

@Component({
  selector: 'app-tedhub-backup',
  templateUrl: './tedhub-backup.component.html',
  standalone: false
})
export class TedhubBackupComponent implements OnInit {
  @ViewChild('tedBackupForm') public tedBackupForm: NgForm;

  @ViewChild('modalDefault') public modalDefault: VueModalComponent;

  /**
   * Error messages to the displayed on screen
   */
  public messages: VueServerMessage[] = [];

  /**
   * VueFormError object to hold the form error details
   */
  public formError: VueFormError = new VueFormError();

  /**
   * i18n keys used by this component
   */
  public translationKeys = [
    'error.UnknownError',
    'error.UnknownResponse',
    'error.RequestDenied',
    'error.ServiceUnavailable',
    'error.ServiceError',
    'error.TokenRequired',
    'error.RequestFormat',
    'error.RequestType',
    'error.InvalidFormSubmit',
    'error.InvalidRandomValue',
    'error.InvalidRegistrationId',
    'error.InvalidSiteId',
    'error.InvalidUsername',
    'error.InvalidResponseCode',
    'error.InvalidAuthorization',
    'error.InvalidChallengeKey',
    'error.InvalidHostId',
    'error.DataUnavailable',
    'error.SiteIdRegIdMismatch',
    'error.SiteUnauthorized',
    'error.RegNotReady',
    'error.RegDisposition',
    'error.RegDoNotExist',
    'message.AuthorizationCodeCopied' ];

  /**
   * Map used to hold the translations
   */
  public translations: Record<string, string> = {};

  /**
   * The error message to be displayed when an attempt is made to save the form with errors
   */
  public invalidSubmitMessage: VueServerMessage;
  
  /**
   * Object to store the request details user enters in the form
   */
  public tedBackUpRequest: TedBackUpRequest;

  /**
   * Content to display in the modal
   */
  public modalContent: string;

  /**
   * Request object for tedBackUp
   */
  public model: {
    siteId?: number;
    registrationId?: number;
    challengeKey?: string;
    userName?: string;
    authorizationCount?: number;
  } = {};

  public errorCodes = new Map<string, string>([
    [ '-1', 'error.UnknownResponse' ],
    [ '1', 'error.RequestDenied' ],
    [ '2', 'error.ServiceUnavailable' ],
    [ '3', 'error.ServiceError' ],
    [ '4', 'error.TokenRequired' ],
    [ '10', 'error.RequestFormat' ],
    [ '11', 'error.RequestType' ],
    [ '12', 'error.InvalidRandomValue' ],
    [ '13', 'error.InvalidRegistrationId' ],
    [ '14', 'error.InvalidSiteId' ],
    [ '15', 'error.InvalidUsername' ],
    [ '16', 'error.InvalidResponseCode' ],
    [ '17', 'error.InvalidAuthorization' ],
    [ '18', 'error.InvalidChallengeKey' ],
    [ '19', 'error.InvalidHostId' ],
    [ '30', 'error.DataUnavailable' ],
    [ '31', 'error.SiteIdRegIdMismatch' ],
    [ '32', 'error.SiteUnauthorized' ],
    [ '33', 'error.RegNotReady' ],
    [ '34', 'error.RegDisposition' ],
    [ '35', 'error.RegDoNotExist' ]
  ]);

  public constructor(
    private tedBackupService: TedBackUpService,
    private vueTranslationService: VueTranslationService,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar
  ) {}

  public ngOnInit(): void {
    this.prepareModel();
    this.translations = this.vueTranslationService.getTranslations(
      'Connect',
      this.translationKeys
    );
    this.invalidSubmitMessage = new VueServerMessage(
      VueServerMessageType.Error,
      this.translations[ 'error.InvalidFormSubmit' ],
      false);
  }

  /**
   * Used to remove the invalid form submit message if the form is valid
   */
  public reValidateForm(): void {
    if (this.tedBackupForm.form.valid === true) {
      if (this.messages.indexOf(this.invalidSubmitMessage) !== -1) {
        this.messages.splice(this.messages.indexOf(this.invalidSubmitMessage), 1);
      }
      this.formError.fieldInError = '';
      this.formError.invalidFormSubmit = false;
    }
  }

  public getAuthorization(): void {
    if (this.tedBackupForm.valid) {
      this.tedBackupService.getAuthorization(this.tedBackUpRequest).subscribe({
        next: (res) => {
          this.checkResponse(res);
        },
        error: () => {
          this.handleError();
        }
      });
    } else {
      this.formError.invalidFormSubmit = true;
      if (this.messages.indexOf(this.invalidSubmitMessage) === -1) {
        this.messages.push(this.invalidSubmitMessage);
      }
    }
  }

  public openModal(): void {
    this.modalDefault.open();
  }

  public prepareModel(): void {
    this.tedBackUpRequest = new TedBackUpRequest();
  }

  /**
   * Method to check the response from the service. Throws an unknown error if the response doesn't
   * contain a authorization code and if not will display the authorization code in a modal
   * @param xmlResponse is the response from the service call
   */
  public checkResponse(xmlResponse: string): void {
    if (xmlResponse.search('authorization') === -1) {
      this.handleErrorCode(this.extractErrorResponseCode(xmlResponse));
      return;
    }
    this.tedBackupForm.reset();
    this.modalContent = this.extractAuthorizationCode(xmlResponse);
    this.openModal();
  }

  /**
   * Method to extract the authorization code as a string
   * @param xmlResponse from the service call
   * @returns the authorization code as a string
   */
  public extractAuthorizationCode(xmlResponse: string): string {
    const firstSubString = xmlResponse.slice(0, xmlResponse.lastIndexOf('</authorization>'));
    const lengthOfFirstSubString = firstSubString.length;
    const secondSubString = firstSubString.slice(firstSubString.lastIndexOf('<authorization>'), lengthOfFirstSubString);
    const authorizationCode = secondSubString.slice(secondSubString.indexOf('>') + 1, secondSubString.length);
    return authorizationCode;
  }

  /**
   * Method to retrieve the error code in the response from the service call8
   * @param xmlResponse Response recieved from the service call
   * @returns the error code in the response
   */
  public extractErrorResponseCode(xmlResponse: string): string {
    const firstSubString = xmlResponse.slice(0, xmlResponse.lastIndexOf('</responseCode>'));
    const lengthOfFirstSubString = firstSubString.length;
    const secondSubString = firstSubString.slice(firstSubString.lastIndexOf('<responseCode>'), lengthOfFirstSubString);
    const errorCode = secondSubString.slice(secondSubString.indexOf('>') + 1, secondSubString.length);
    return errorCode;
  }

  /**
   * Method to copy the authorization code to the users clipboard
   */
  public copyAuthorizationCodeToClipboard(): void {
    this.clipboard.copy(this.modalContent);
    const message = this.translations[ 'message.AuthorizationCodeCopied' ];
    this.snackBar.open(message);
  }

  /**
   * This method is used to handle unknown server side errors during the service calls
   */
  private handleError(): void {
    this.messages.push(
      new VueServerMessage(
        VueServerMessageType.Error,
        this.translations[ 'error.UnknownError' ],
        true)
    );
  }

  /**
   * This method is used to handle server side errors during the service calls
   * @param responseCode extracted from the service call response
   */
  private handleErrorCode(responseCode: string): void {
    if (!this.errorCodes.get(responseCode)) {
      this.handleError();
      return;
    }
    this.messages.push(
      new VueServerMessage(
        VueServerMessageType.Error,
        this.translations[ this.errorCodes.get(responseCode) ],
        true)
    );
  }
}
